import React, { useEffect, useState } from 'react';
import { alpha, Box, IconButton, Stack, Theme, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import IconMi from 'components/common/icon/IconMi';
import { setInputToastRightClearance } from 'store/conciergeSlice';

const TOAST_RIGHT = 24;
const TOAST_WIDTH = 360;
const TOAST_GAP = 20;

interface Props {
    unansweredCount: number;
    onOpen: () => void;
    onDismiss: () => void;
}

export default function ConciergeInputToast({ unansweredCount, onOpen, onDismiss }: Props) {
    const [visible, setVisible] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 350);
        return () => clearTimeout(timer);
    }, []);

    // Other bottom-right widgets (the rating prompt) read this to slide left
    // of the toast instead of under it.
    useEffect(() => {
        dispatch(setInputToastRightClearance(TOAST_RIGHT + TOAST_WIDTH + TOAST_GAP));
        return () => {
            dispatch(setInputToastRightClearance(0));
        };
    }, [dispatch]);

    useEffect(() => {
        if (unansweredCount === 0) {
            setVisible(false);
            const timer = setTimeout(onDismiss, 300);
            return () => clearTimeout(timer);
        }
    }, [unansweredCount, onDismiss]);

    const handleOpen = () => {
        setVisible(false);
        setTimeout(onDismiss, 300);
        onOpen();
    };

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setVisible(false);
        setTimeout(onDismiss, 300);
    };

    const questionWord = unansweredCount === 1 ? 'clarifying question' : 'clarifying questions';

    return (
        <Box
            role="status"
            aria-live="polite"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                }
            }}
            sx={{
                position: 'fixed',
                right: `${TOAST_RIGHT}px`,
                bottom: '24px',
                width: `${TOAST_WIDTH}px`,
                zIndex: 1200,
                cursor: 'pointer',
                borderRadius: '8px',
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                outline: 'none',
                display: 'flex',
                transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 32px))',
                opacity: visible ? 1 : 0,
                transition: 'transform 0.3s ease, opacity 0.3s ease',
                '&:hover': {
                    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
                    '& .open-link': { textDecoration: 'underline' },
                },
                '&:focus-visible': {
                    outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                },
            }}
        >
            {/* Teal left accent bar */}
            <Box sx={{ width: '4px', flexShrink: 0, bgcolor: 'primary.main' }} />

            <Stack sx={{ p: 1.5, flexGrow: 1, minWidth: 0 }} spacing={0.5}>
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconMi icon="user-help" sx={{ fontSize: '16px', color: 'primary.main', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'primary.dark', flexGrow: 1 }}>
                        Concierge needs your input
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        aria-label="Dismiss"
                        sx={{
                            p: 0.25,
                            color: 'text.secondary',
                            '&:hover': { bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08) },
                        }}
                    >
                        <IconMi icon="times" sx={{ fontSize: '14px' }} />
                    </IconButton>
                </Stack>

                {/* Body */}
                <Typography sx={{ fontSize: '13px', color: 'text.primary', lineHeight: 1.5 }}>
                    Please answer <strong>{unansweredCount}</strong> {questionWord} to help Concierge interpret this
                    report's data accurately.
                </Typography>

                {/* Link */}
                <Typography className="open-link" sx={{ fontSize: '13px', color: 'primary.main', mt: 0.25 }}>
                    Open Concierge →
                </Typography>
            </Stack>
        </Box>
    );
}
